import { type CSSProperties, memo, useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board } from '@air/api/types';
import { ChevronRight as ChevronRightIcon } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { GalleryItemType, type ItemProps } from '~/components/Gallery/types';
import { BoardTableRow, type BoardTableRowProps } from '~/components/TableView/BoardTableRow';
import { MobileTableRowContainer } from '~/components/TableView/MobileTableRow/MobileTableRowContainer';
import { PrivateBoardNameCell } from '~/components/TableView/PrivateTableView/PrivateBoardNameCell';
import { PrivateDraggableBoardTableRowContainer } from '~/components/TableView/PrivateTableView/PrivateDraggableBoardTableRowContainer';
import { useRenamingContext } from '~/components/TableView/PrivateTableView/TableViewItemRenamingContext';
import { PrivateBoardCustomFieldTableCell } from '~/components/TableView/TableRow/CustomFieldTableCell/PrivateBoardCustomFieldTableCell';
import { DesktopTableRowContainer } from '~/components/TableView/TableRow/DesktopTableRowContainer';
import { type BasicTableRowProps } from '~/components/TableView/types';
import { useGetPrivateTableViewBoardMenuOptionsCallback } from '~/components/TableView/useGetPrivateTableViewBoardMenuOptionsCallback';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { TABLE_ROW } from '~/constants/testIDs';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useOpenBoardInNewTab } from '~/hooks/useOpenBoardInNewTab';
import { usePrivateBoardMetadataMenuOptions } from '~/hooks/usePrivateBoardMetadataMenuOptions';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';

export type PrivateBoardTableRowProps = {
  /**
   * `react-virtualized` warns that `style` property should be included in the rendered cell for positioning
   * so we've separated it from the `tx` prop
   * */
  style: CSSProperties;
} & BasicTableRowProps<Board>;

export const PrivateBoardTableRow = memo(({ data: board, style }: PrivateBoardTableRowProps) => {
  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { setIsRenaming, getKeyboardListenerProps } = useRenamingContext();
  const { getTableViewBoardMenuOptionsCallback } = useGetPrivateTableViewBoardMenuOptionsCallback({
    onRename: () => setIsRenaming(board.id),
  });
  const metadataMenuOptions = usePrivateBoardMetadataMenuOptions(board);
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();

  const { isAboveMediumScreen } = useBreakpointsContext();

  const baseGetItemMenuOptions = useMemo(
    () => getTableViewBoardMenuOptionsCallback({ item: board }),
    [board, getTableViewBoardMenuOptionsCallback],
  );

  const getItemMenuOptions = useMemo<GetItemMenuOptions>(
    () =>
      ({ trackLocation }) => [...baseGetItemMenuOptions({ trackLocation }), ...metadataMenuOptions],
    [baseGetItemMenuOptions, metadataMenuOptions],
  );

  const { goToBoardPage } = useGoToBoardPage();
  const { openBoardInNewTab } = useOpenBoardInNewTab();
  const onItemClick = useCallback(
    (trackLocation: AirActionTrackingLocation) => goToBoardPage({ board, trackLocation }),
    [board, goToBoardPage],
  );
  const onItemCmdClick = useCallback(
    (trackLocation: AirActionTrackingLocation) => openBoardInNewTab({ board, trackLocation }),
    [board, openBoardInNewTab],
  );

  const memoizedBoardProps: ItemProps = useMemo(
    () => ({
      isSelectable: true,
      onItemClick,
      onItemCmdClick,
      getSelectionMenuOptions,
      getItemMenuOptions,
    }),
    [getItemMenuOptions, getSelectionMenuOptions, onItemClick, onItemCmdClick],
  );

  const renderNameCell: BoardTableRowProps<Board>['renderNameCell'] = useCallback(
    ({ isSelected, isHovering }) => (
      <PrivateBoardNameCell isHovering={isHovering} isSelected={isSelected} board={board} {...memoizedBoardProps} />
    ),
    [board, memoizedBoardProps],
  );

  const keyboardListenerProps = useMemo(() => getKeyboardListenerProps(board.id), [board.id, getKeyboardListenerProps]);

  return isAboveMediumScreen ? (
    <DesktopTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
      data-testid={`${TABLE_ROW}-${GalleryItemType.board}`}
      data-title={board.title}
    >
      <PrivateDraggableBoardTableRowContainer board={board}>
        <BoardTableRow
          renderNameCell={renderNameCell}
          data={board}
          boardProps={memoizedBoardProps}
          CustomFieldTableCell={PrivateBoardCustomFieldTableCell}
          canSelect={true}
          keyboardListenerProps={keyboardListenerProps}
        />
      </PrivateDraggableBoardTableRowContainer>
    </DesktopTableRowContainer>
  ) : (
    <MobileTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
    >
      {renderNameCell({ isSelected: false, isHovering: false })}
      <IconButton
        appearance="ghost"
        color="grey"
        icon={ChevronRightIcon}
        onClick={() => {
          memoizedBoardProps.onItemClick('table-cell');
        }}
        size="extra-large"
        className="h-full"
        label="View board"
      />
    </MobileTableRowContainer>
  );
}, isEqual);

PrivateBoardTableRow.displayName = 'PrivateBoardTableRow';
